import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Icon } from './Icon';
import { Spinner } from './Spinner';
import type { SelectOption } from './Select';

interface FilterGroupProps {
  title: string;
  /** 고른 값들. undefined = 전체(모두 선택), [] = 아무것도 고르지 않음 */
  value: string[] | undefined;
  options: SelectOption[];
  /** 모두 고르면 undefined(전체)로 접어서 넘긴다 */
  onChange: (value: string[] | undefined) => void;
  allLabel?: string;
  /** 선택지를 불러오는 중이면 체크박스 대신 스피너 */
  isLoading?: boolean;
}

/**
 * Figma 필터 드로어의 접이식 섹션 — 소제목 + 접기 버튼 + 체크박스 목록(다중 선택).
 * '전체'는 모두 선택/해제 토글이고, 항목이 모두 선택되면 '전체'도 체크된다.
 */
export function FilterGroup({
  title,
  value,
  options,
  onChange,
  allLabel = '전체',
  isLoading = false,
}: FilterGroupProps) {
  const selected = value ?? options.map((option) => option.value);
  const isAll = value === undefined;

  const toggle = (optionValue: string, checked: boolean) => {
    const next = checked
      ? [...selected, optionValue]
      : selected.filter((item) => item !== optionValue);
    onChange(next.length === options.length ? undefined : next);
  };

  return (
    <CollapsiblePrimitive.Root
      defaultOpen
      className="group flex w-full flex-col gap-3 px-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-subtitle-16 font-bold text-fg-primary">{title}</h3>
        <CollapsiblePrimitive.Trigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={`${title} 접기`}>
            <Icon
              name="chevronUpSmall"
              size={16}
              className="transition-transform group-data-[state=closed]:rotate-180"
            />
          </Button>
        </CollapsiblePrimitive.Trigger>
      </div>
      <CollapsiblePrimitive.Content className="flex flex-col gap-3">
        {isLoading ? (
          <div className="flex items-center gap-2 py-1 text-body-14 font-medium text-fg-light">
            <Spinner size="sm" />
            불러오는 중…
          </div>
        ) : (
          <>
            <Checkbox
              label={allLabel}
              checked={isAll}
              onCheckedChange={(checked) => onChange(checked ? undefined : [])}
            />
            {options.map((option) => (
              <Checkbox
                key={option.value}
                label={option.label}
                checked={selected.includes(option.value)}
                onCheckedChange={(checked) => toggle(option.value, checked)}
              />
            ))}
          </>
        )}
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}
